import { create } from 'zustand';
import type { PublicUser } from '@jaluzi/types';
import { persistent, type CalcLayoutMode, type OrderLayoutMode } from '../lib/storage.js';
import { api } from '../lib/api.js';

/**
 * Стор пользовательских предпочтений UI.
 *
 * Часть настроек «привязана к человеку», а не к устройству — стиль формы замера
 * (calcLayoutMode) и вид карточки заказа (orderLayoutMode). Их источник истины —
 * сервер (User.uiPrefs): при входе на другом телефоне сотрудник видит свой выбор.
 * localStorage используется как быстрый/офлайн-кеш: значение мгновенно доступно
 * до ответа /auth/me, а при смене — сохраняется и локально, и на сервере.
 *
 * calcShowImages остаётся чисто локальным (мелкая настройка отображения).
 */
const VALID_CALC: CalcLayoutMode[] = ['wizard', 'linear', 'accordion', 'cards'];
const VALID_ORDER: OrderLayoutMode[] = ['single', 'tabs2', 'tabs3', 'tabs4'];

/**
 * Best-effort сохранение UI-настроек на сервер. Если сети нет — значение уже
 * лежит в localStorage, а на сервер уедет при следующем удачном изменении.
 * Ошибку глотаем намеренно: настройка не критична, тост не нужен.
 */
function saveUiPref(uiPrefs: { calcLayoutMode?: CalcLayoutMode; orderLayoutMode?: OrderLayoutMode; overdueHighlight?: boolean }) {
  void api('/v1/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ uiPrefs }),
  }).catch(() => { /* офлайн/ошибка — не мешаем пользователю */ });
}

interface PrefsState {
  calcShowImages: boolean;
  setCalcShowImages: (v: boolean) => void;
  calcLayoutMode: CalcLayoutMode;
  setCalcLayoutMode: (v: CalcLayoutMode) => void;
  orderLayoutMode: OrderLayoutMode;
  setOrderLayoutMode: (v: OrderLayoutMode) => void;
  /** Подсветка просроченных замеров (красная рамка + статичный красный бейдж). true (дефолт)
   *  — показывать; false — сотрудник отключил в профиле. Синхронизируется на сервер. */
  overdueHighlight: boolean;
  setOverdueHighlight: (v: boolean) => void;
  /**
   * Подтянуть настройки из профиля с сервера (после /auth/me или логина).
   * Сервер — источник истины: если у него есть значение, оно перебивает
   * локальное и на этом устройстве. Если сервер пуст (легаси-пользователь до
   * появления фичи) — локальный выбор не трогаем, он уедет на сервер при
   * следующем изменении.
   */
  hydrateFromUser: (user: PublicUser | null | undefined) => void;
}

export const usePrefs = create<PrefsState>((set) => ({
  calcShowImages: persistent.getCalcShowImages(),
  setCalcShowImages: (v) => {
    persistent.setCalcShowImages(v);
    set({ calcShowImages: v });
  },
  calcLayoutMode: persistent.getCalcLayoutMode(),
  setCalcLayoutMode: (v) => {
    persistent.setCalcLayoutMode(v);
    set({ calcLayoutMode: v });
    saveUiPref({ calcLayoutMode: v });
  },
  orderLayoutMode: persistent.getOrderLayoutMode(),
  setOrderLayoutMode: (v) => {
    persistent.setOrderLayoutMode(v);
    set({ orderLayoutMode: v });
    saveUiPref({ orderLayoutMode: v });
  },
  overdueHighlight: persistent.getOverdueHighlight(),
  setOverdueHighlight: (v) => {
    persistent.setOverdueHighlight(v);
    set({ overdueHighlight: v });
    saveUiPref({ overdueHighlight: v });
  },
  hydrateFromUser: (user) => {
    const server = user?.uiPrefs;
    if (!server) return;
    const patch: Partial<Pick<PrefsState, 'calcLayoutMode' | 'orderLayoutMode' | 'overdueHighlight'>> = {};
    if (server.calcLayoutMode && VALID_CALC.includes(server.calcLayoutMode)) {
      persistent.setCalcLayoutMode(server.calcLayoutMode);
      patch.calcLayoutMode = server.calcLayoutMode;
    }
    if (server.orderLayoutMode && VALID_ORDER.includes(server.orderLayoutMode)) {
      persistent.setOrderLayoutMode(server.orderLayoutMode);
      patch.orderLayoutMode = server.orderLayoutMode;
    }
    if (typeof server.overdueHighlight === 'boolean') {
      persistent.setOverdueHighlight(server.overdueHighlight);
      patch.overdueHighlight = server.overdueHighlight;
    }
    if (Object.keys(patch).length) set(patch);
  },
}));
